<template>
    <section class="relative py-16 sm:py-20 lg:py-28 overflow-hidden">

        <!-- Background -->
        <div class="absolute inset-0 pointer-events-none">
            <div class="absolute top-0 left-1/3 w-96 h-96 rounded-full opacity-16"
                 style="background: radial-gradient(circle, #a75ded 0%, transparent 70%); filter: blur(80px);"></div>
            <div class="absolute bottom-0 right-1/3 w-80 h-80 rounded-full opacity-16"
                 style="background: radial-gradient(circle, #06b6d4 0%, transparent 70%); filter: blur(80px);"></div>
            <div class="absolute top-1/2 left-0 w-72 h-72 rounded-full opacity-12"
                 style="background: radial-gradient(circle, #e879f9 0%, transparent 70%); filter: blur(80px);"></div>
        </div>

        <!-- Top divider -->
        <div class="absolute top-0 left-0 right-0 h-px"
             style="background: linear-gradient(90deg, transparent 0%, rgba(232,121,249,0.6) 25%, rgba(167,93,237,0.8) 50%, rgba(6,182,212,0.6) 75%, transparent 100%);"></div>

        <div class="relative z-10 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">

            <!-- Section Label -->
            <div class="flex justify-center mb-6">
                <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-bold tracking-[0.15em] uppercase scroll-reveal"
                     style="border-color: rgba(167,93,237,0.5); background: rgba(167,93,237,0.10); color: #c084fc;">
                    <span class="w-1.5 h-1.5 rounded-full animate-pulse inline-block" style="background:#a75ded; box-shadow:0 0 8px #a75ded;"></span>
                    {{ t('howItWorksLabel') }}
                </div>
            </div>

            <!-- Headline -->
            <div class="text-center mb-14 scroll-reveal">
                <h2 class="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
                    <span class="text-white block">{{ t('howItWorksTitle') }}</span>
                    <span class="block mt-2"
                          style="background:linear-gradient(135deg,#7c3aed,#a75ded,#e879f9,#06b6d4);-webkit-background-clip:text;background-clip:text;color:transparent;">
                        {{ t('howItWorksSubtitle') }}
                    </span>
                </h2>
            </div>

            <!-- Desktop: Horizontal Steps with connector line -->
            <div class="hidden lg:block mb-10">
                <div class="relative flex items-start">
                    <!-- Animated connector line -->
                    <div class="absolute top-8 left-[12.5%] right-[12.5%] h-[2px]"
                         style="background: linear-gradient(90deg, #7c3aed, #a75ded, #06b6d4, #e879f9); box-shadow: 0 0 12px rgba(167,93,237,0.5);"></div>

                    <div class="grid grid-cols-4 gap-4 w-full">
                        <div v-for="(step, idx) in steps" :key="idx"
                             class="group flex flex-col items-center text-center"
                             @mouseenter="activeStep = idx"
                             @mouseleave="activeStep = -1">

                            <!-- Number circle -->
                            <div class="relative z-10 w-16 h-16 rounded-full flex items-center justify-center font-black text-xl mb-6 transition-all duration-500 cursor-pointer"
                                 :style="{
                                     background: activeStep === idx ? step.circleGradActive : step.circleGrad,
                                     boxShadow: activeStep === idx ? step.circleShadowActive : step.circleShadow,
                                     color: '#fff',
                                     transform: activeStep === idx ? 'scale(1.18)' : 'scale(1)',
                                 }">
                                {{ idx + 1 }}
                            </div>

                            <!-- Content card -->
                            <div class="rounded-2xl p-6 w-full border transition-all duration-500"
                                 :style="{
                                     background: activeStep === idx ? step.cardBgActive : step.cardBg,
                                     borderColor: activeStep === idx ? step.borderActive : step.borderColor,
                                     boxShadow: activeStep === idx ? step.cardShadow : 'none',
                                 }">
                                <div class="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3"
                                     :style="{ background: step.iconGrad, boxShadow: step.iconShadow }">
                                    <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" :d="step.iconPath"/>
                                    </svg>
                                </div>
                                <h3 class="text-base font-black text-white mb-2 transition-colors duration-300"
                                    :style="{ color: activeStep === idx ? step.accentColor : '#fff' }">
                                    {{ step.title }}
                                </h3>
                                <p class="text-white/60 text-sm leading-relaxed">{{ step.description }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Mobile: Vertical Steps -->
            <div class="lg:hidden space-y-6">
                <div v-for="(step, idx) in steps" :key="idx" class="flex gap-5 scroll-reveal">
                    <!-- Left: number + line -->
                    <div class="flex flex-col items-center">
                        <div class="w-12 h-12 rounded-full flex items-center justify-center font-black text-base flex-shrink-0"
                             :style="{ background: step.circleGrad, color: '#fff', boxShadow: step.circleShadow }">
                            {{ idx + 1 }}
                        </div>
                        <div v-if="idx < steps.length - 1"
                             class="w-0.5 flex-1 mt-2 rounded-full min-h-[32px]"
                             :style="{ background: `linear-gradient(to bottom, ${step.lineColor}, rgba(10,4,22,0.1))` }"></div>
                    </div>

                    <!-- Content -->
                    <div class="flex-1 pb-4 rounded-2xl p-5 border"
                         :style="{ background: step.cardBg, borderColor: step.borderColor }">
                        <div class="flex items-center gap-3 mb-2">
                            <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                 :style="{ background: step.iconGrad, boxShadow: step.iconShadow }">
                                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" :d="step.iconPath"/>
                                </svg>
                            </div>
                            <h3 class="text-base font-black text-white">{{ step.title }}</h3>
                        </div>
                        <p class="text-white/60 text-sm leading-relaxed">{{ step.description }}</p>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useTranslation } from '@/composables/useTranslation'

const { t } = useTranslation()
const activeStep = ref(-1)

const steps = [
    {
        title: 'Connect Your Wallet',
        description: 'Securely integrate your Web3 wallet with support for MetaMask, WalletConnect, and leading blockchain wallet infrastructures.',
        iconPath: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.1-1.1m-.758-4.9a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
        circleGrad: 'linear-gradient(135deg,#7c3aed,#a75ded)',
        circleGradActive: 'linear-gradient(135deg,#5b21b6,#c084fc)',
        circleShadow: '0 4px 20px rgba(124,58,237,0.50)',
        circleShadowActive: '0 0 32px rgba(124,58,237,0.80), 0 0 64px rgba(124,58,237,0.35)',
        iconGrad: 'linear-gradient(135deg,#7c3aed,#a75ded)',
        iconShadow: '0 4px 18px rgba(124,58,237,0.55)',
        cardBg: 'rgba(124,58,237,0.10)',
        cardBgActive: 'rgba(124,58,237,0.20)',
        borderColor: 'rgba(124,58,237,0.25)',
        borderActive: 'rgba(124,58,237,0.55)',
        cardShadow: '0 20px 48px rgba(124,58,237,0.28)',
        accentColor: '#a78bfa',
        lineColor: '#a75ded',
    },
    {
        title: 'Allocate Digital Capital',
        description: 'Deploy assets into your preferred DeFi strategy with flexible allocation structures and scalable participation options.',
        iconPath: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
        circleGrad: 'linear-gradient(135deg,#0e7490,#06b6d4)',
        circleGradActive: 'linear-gradient(135deg,#0369a1,#38bdf8)',
        circleShadow: '0 4px 20px rgba(6,182,212,0.50)',
        circleShadowActive: '0 0 32px rgba(6,182,212,0.80), 0 0 64px rgba(6,182,212,0.35)',
        iconGrad: 'linear-gradient(135deg,#0e7490,#06b6d4)',
        iconShadow: '0 4px 18px rgba(6,182,212,0.55)',
        cardBg: 'rgba(6,182,212,0.10)',
        cardBgActive: 'rgba(6,182,212,0.20)',
        borderColor: 'rgba(6,182,212,0.25)',
        borderActive: 'rgba(6,182,212,0.55)',
        cardShadow: '0 20px 48px rgba(6,182,212,0.28)',
        accentColor: '#67e8f9',
        lineColor: '#06b6d4',
    },
    {
        title: 'AI-Powered Optimization',
        description: 'Advanced algorithms continuously enhance portfolio efficiency through real-time blockchain analytics and intelligent market monitoring.',
        iconPath: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
        circleGrad: 'linear-gradient(135deg,#c026d3,#e879f9)',
        circleGradActive: 'linear-gradient(135deg,#9d174d,#f9a8d4)',
        circleShadow: '0 4px 20px rgba(217,70,239,0.50)',
        circleShadowActive: '0 0 32px rgba(217,70,239,0.80), 0 0 64px rgba(217,70,239,0.35)',
        iconGrad: 'linear-gradient(135deg,#c026d3,#e879f9)',
        iconShadow: '0 4px 18px rgba(217,70,239,0.55)',
        cardBg: 'rgba(217,70,239,0.10)',
        cardBgActive: 'rgba(217,70,239,0.20)',
        borderColor: 'rgba(217,70,239,0.25)',
        borderActive: 'rgba(217,70,239,0.55)',
        cardShadow: '0 20px 48px rgba(217,70,239,0.28)',
        accentColor: '#f0abfc',
        lineColor: '#e879f9',
    },
    {
        title: 'Generate Digital Rewards',
        description: 'Receive automated yield allocations, ecosystem incentives, and performance-based blockchain reward distributions.',
        iconPath: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
        circleGrad: 'linear-gradient(135deg,#4f46e5,#818cf8)',
        circleGradActive: 'linear-gradient(135deg,#3730a3,#a5b4fc)',
        circleShadow: '0 4px 20px rgba(99,102,241,0.50)',
        circleShadowActive: '0 0 32px rgba(99,102,241,0.80), 0 0 64px rgba(99,102,241,0.35)',
        iconGrad: 'linear-gradient(135deg,#4f46e5,#818cf8)',
        iconShadow: '0 4px 18px rgba(99,102,241,0.55)',
        cardBg: 'rgba(99,102,241,0.10)',
        cardBgActive: 'rgba(99,102,241,0.20)',
        borderColor: 'rgba(99,102,241,0.25)',
        borderActive: 'rgba(99,102,241,0.55)',
        cardShadow: '0 20px 48px rgba(99,102,241,0.28)',
        accentColor: '#a5b4fc',
        lineColor: '#818cf8',
    },
]

onMounted(() => {
    if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(entries => {
            entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in-view') })
        }, { threshold: 0.1 })
        document.querySelectorAll('.scroll-reveal').forEach(el => io.observe(el))
    } else {
        document.querySelectorAll('.scroll-reveal').forEach(el => el.classList.add('in-view'))
    }
})
</script>

<style scoped>
.scroll-reveal { opacity:0; transform:translateY(32px); transition:opacity 0.7s ease,transform 0.7s ease; }
.scroll-reveal.in-view { opacity:1; transform:none; }
@media(prefers-reduced-motion:reduce){ .scroll-reveal{ opacity:1;transform:none;transition:none; } }
</style>
